import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Star } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProductCard = ({ product, inWishlist = false, onWishlistToggle, onAddToCart, averageRating = 0, totalReviews = 0 }) => {
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const galleryMedia = useMemo(
    () => [
      ...((product.image_urls || []).map((url, index) => ({
        id: `image-${index}`,
        type: 'image',
        url
      }))),
      ...((product.video_urls || []).map((url, index) => ({
        id: `video-${index}`,
        type: 'video',
        url
      })))
    ],
    [product.image_urls, product.video_urls]
  );
  const fallbackMedia = product.image_url
    ? [{ id: 'image-fallback', type: 'image', url: product.image_url }]
    : [];
  const previewMedia = galleryMedia.length > 0 ? galleryMedia : fallbackMedia;
  const activeMedia = previewMedia[activeMediaIndex] || previewMedia[0] || null;

  useEffect(() => {
    if (!isHovered || previewMedia.length <= 1) {
      setActiveMediaIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveMediaIndex((currentIndex) => (currentIndex + 1) % previewMedia.length);
    }, 700);

    return () => window.clearInterval(intervalId);
  }, [isHovered, previewMedia]);

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please log in to manage your wishlist');
      navigate('/login');
      return;
    }
    try {
      if (inWishlist) {
        await axios.delete(`${BACKEND_URL}/api/wishlist/${product.id}`, { withCredentials: true });
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${BACKEND_URL}/api/wishlist`, { product_id: product.id }, { withCredentials: true });
        toast.success('Added to wishlist');
      }
      if (onWishlistToggle) onWishlistToggle(product.id, !inWishlist);
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please log in to add items to your cart');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/cart`, { product_id: product.id, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
      if (onAddToCart) onAddToCart();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Your session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  return (
    <Link to={`/products/${product.id}`} className="group" data-testid={`product-card-${product.id}`}>
      <div
        className="heritage-card vintage-card overflow-hidden rounded-[2px] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(74,47,28,0.14)]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative aspect-square overflow-hidden">
          {activeMedia?.type === 'video' ? (
            <video
              src={activeMedia.url}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <img
              src={activeMedia?.url}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}
          {previewMedia.length > 1 && (
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/35 px-2 py-1">
              {previewMedia.map((media, index) => (
                <span
                  key={media.id}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === activeMediaIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/60'
                  }`}
                />
              ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-3 top-3 bg-[rgba(255,248,236,0.96)] shadow-md hover:bg-[#fffdf7]"
            onClick={handleWishlist}
            data-testid={`wishlist-toggle-${product.id}`}
          >
            <Heart
              className={`h-5 w-5 transition-colors ${
                inWishlist ? 'fill-current' : ''
              }`}
              style={{ color: inWishlist ? 'var(--primary)' : '#6B7280' }}
            />
          </Button>
        </div>
        <div className="p-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#8e755d]">{product.category}</p>
          <h3 className="heritage-title mb-2 line-clamp-1 text-2xl">{product.name}</h3>

          {totalReviews > 0 && (
            <div className="flex items-center space-x-2 mb-3">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-4 w-4 ${
                      star <= Math.floor(averageRating)
                        ? 'fill-current text-amber-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-[#7b6457]">({totalReviews})</span>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <p className="heritage-title text-2xl text-[#4b2e24]">
              {formatPrice(product.price)}
            </p>
            <Button
              onClick={handleAddToCart}
              className="heritage-button rounded-none"
              size="sm"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
